import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Input,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { LazyItem, LazyItemType } from "@obg/common.lazy-loader";
import { UserSummaryConfigService } from "@obg/mat.ui.common";
import { AlternativeLayoutConfigService } from "@obg/ngx.common";
import {
	AuthTransactionSelector,
	CORE_ROUTE,
	CoreState,
	CoreUiInteractionEvent,
	Jurisdiction,
	JurisdictionSelector,
	SessionSelector,
	UiInteraction,
} from "@obg/ngx.core";
import { CoreContentState } from "@obg/ngx.core.content";
import { coerceBoolean } from "@obg/ngx.global.evo";
import { ignoreNil } from "@obg/rxjs";
import { combineLatest, Observable } from "rxjs";
import { filter, map, take } from "rxjs/operators";

import { OMS_TRACKING_DATA } from "../oms/oms-summary.const";
import { HeaderAction, TrackingContext } from "../tracking.model";

import { AccountControlConfigService } from "./account-control.config";

const HEADER_EVENT: UiInteraction = {
	event: CoreUiInteractionEvent.Navigate,
	context: TrackingContext.HeaderInteractionContext,
};
@Component({
	selector: "obg-app-m-account-control-container",
	templateUrl: "./account-control.container.html",
	styleUrls: ["./account-control.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class AccountControlContainer implements OnInit {
	@HostBinding("class.obg-m-account-control-container") hostCssClass = true;

	@Input() @coerceBoolean() balanceHidden = false;
	@Input() @coerceBoolean() depositButtonHidden = false;
	@Input() @coerceBoolean() gamingLayout = false;
	@Input() @coerceBoolean() hideWithdrawButton = false;

	@Input() title: string | undefined;

	jurisdiction$: Observable<Jurisdiction | undefined>;
	alternativeLoginForm: LazyItem | undefined;
	isAlternativeButtonStyleEnabled: boolean | undefined;

	readonly isAuth$ = this.store.select(this.sessionSelector.isAuthenticated());
	readonly isAuthenticating$ = combineLatest([
		this.store.select(this.authTransactionSelector.isBusy()),
		this.store.select(this.sessionSelector.isBusy()),
	]).pipe(map(([isAuthTransactionBusy, isAuthBusy]) => isAuthTransactionBusy || isAuthBusy));

	readonly loginRoute = CORE_ROUTE.auth.login;
	readonly registerRoute = CORE_ROUTE.registration.register;
	readonly depositRoute = CORE_ROUTE.payment.deposit;
	readonly withdrawRoute = CORE_ROUTE.payment.withdraw;
	readonly activeRewardsRoute = CORE_ROUTE.reward.active;
	readonly messagesRoute = CORE_ROUTE.oms.messages;

	readonly loginTrackingData = { ...HEADER_EVENT, data: { ItemClicked: HeaderAction.Login } };
	readonly openAccountTrackingData = { ...HEADER_EVENT, data: { ItemClicked: HeaderAction.OpenAccount } };
	readonly bonusTrackingData = { ...HEADER_EVENT, data: { ItemClicked: HeaderAction.Bonuses } };
	readonly depositTrackingData = { ...HEADER_EVENT, data: { ItemClicked: HeaderAction.Deposit } };
	readonly withdrawTrackingData = { ...HEADER_EVENT, data: { ItemClicked: HeaderAction.Withdraw } };
	readonly omsTrackingData = OMS_TRACKING_DATA;

	readonly isRegisterButtonVisible = this.accountControlConfig.isRegisterButtonVisible();
	readonly withdrawButtonEnabled = !this.depositButtonHidden && this.accountControlConfig.getWithdrawLayoutEnabled();
	readonly isBonusButtonEnabled$ = this.userSummaryConfigService.getBonusVisible$().pipe(ignoreNil());

	constructor(
		private store: Store<CoreState & CoreContentState>,
		private sessionSelector: SessionSelector,
		private authTransactionSelector: AuthTransactionSelector,
		private accountControlConfig: AccountControlConfigService,
		private changeDetector: ChangeDetectorRef,
		private alternativeLayoutConfig: AlternativeLayoutConfigService,
		private userSummaryConfigService: UserSummaryConfigService
	) {
		this.jurisdiction$ = this.store.select(JurisdictionSelector.get).pipe(
			filter((jurisdiction) => !!jurisdiction),
			take(1),
			map((jurisdiction) => jurisdiction)
		);
	}

	ngOnInit() {
		this.isAlternativeButtonStyleEnabled = this.alternativeLayoutConfig.alternativeButtonsEnabled();
		if (this.accountControlConfig.isAlternativeLoginEnabled()) {
			this.alternativeLoginForm = {
				name: "HeaderLoginModule",
				path: () => import("@obg/common.login/login/header-login/header-login.module"),
				type: LazyItemType.module,
			};
		}
		this.changeDetector.detectChanges();
	}
}
